import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { CreditCard, Check, Loader2, AlertCircle, Sparkles, LogIn } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AuthForm } from '../auth/AuthForm';
interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  priceId: string;
  popular?: boolean;
  discount?: string;
  isSubscription?: boolean;
}
const packages: CreditPackage[] = [{
  id: 'starter',
  name: 'Starter Pack',
  credits: 5,
  price: 1.99,
  priceId: 'price_1SMcjLBaZtBtpc8wLhQnNYvZ'
}, {
  id: 'pro',
  name: 'Pro Pack',
  credits: 10,
  price: 4.75,
  priceId: 'price_1SMcjLBaZtBtpc8wdu17Twd2',
  popular: true,
  discount: 'Best Value'
}, {
  id: 'unlimited',
  name: 'Unlimited Plan',
  credits: -1,
  price: 6.75,
  priceId: 'price_1SMcjLBaZtBtpc8wVE8mHnJM',
  discount: 'Monthly',
  isSubscription: true
}];
interface CreditPurchaseProps {
  currentCredits?: number;
  onPurchaseComplete?: () => void;
}
export function CreditPurchase({
  currentCredits = 0,
  onPurchaseComplete
}: CreditPurchaseProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGuestMode, setIsGuestMode] = useState(false);
  useEffect(() => {
    checkAuthStatus();
  }, []);
  const checkAuthStatus = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) {
      const guestMode = localStorage.getItem('guestMode') === 'true';
      setIsGuestMode(guestMode);
    }
  };
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setError(null);
      if (onPurchaseComplete) {
        onPurchaseComplete();
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('canceled') === 'true') {
      setError('Payment was canceled.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [onPurchaseComplete]);
  const handlePurchase = async (pkg: CreditPackage) => {
    setLoading(pkg.id);
    setError(null);
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to purchase credits');
      }
      const response = await fetch('https://api.scribetok.com/api/payments/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          price_id: pkg.priceId,
          user_email: user.email
        })
      });
      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }
      const {
        url
      } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };
  if (isGuestMode) {
    return <div className="space-y-6" data-id="element-763">
        <div className="text-center py-6" data-id="element-764">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4" data-id="element-765">
            <LogIn size={24} className="text-primary" data-id="element-766" />
          </div>
          <h3 className="text-xl font-semibold mb-2" data-id="element-767">
            Sign In to Purchase Credits
          </h3>
          <p className="text-muted-foreground" data-id="element-768">
            Create an account or sign in to buy credits and unlock comment
            analysis
          </p>
        </div>
        <AuthForm onLoginSuccess={() => {
        window.location.reload();
      }} data-id="element-769" />
      </div>;
  }
  return <div className="space-y-6" data-id="element-770">
      {error && <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3" data-id="element-771">
          <AlertCircle size={20} className="text-destructive flex-shrink-0 mt-0.5" data-id="element-772" />
          <p className="text-sm text-destructive" data-id="element-773">{error}</p>
        </div>}
      <div className="bg-secondary/20 p-4 rounded-lg" data-id="element-774">
        <div className="flex items-center justify-between mb-2" data-id="element-775">
          <h3 className="font-medium" data-id="element-776">Current Balance</h3>
          <div className="flex items-center gap-2" data-id="element-777">
            <Sparkles size={16} className="text-primary" data-id="element-778" />
            <span className="text-2xl font-bold" data-id="element-779">{currentCredits}</span>
            <span className="text-muted-foreground" data-id="element-780">credits</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground" data-id="element-781">
          Each credit covers analysis of 100 comments
        </p>
      </div>
      <div data-id="element-782">
        <h3 className="text-lg font-semibold mb-2" data-id="element-783">Purchase Credits</h3>
        <p className="text-sm text-muted-foreground mb-4" data-id="element-784">
          Choose a package that fits your needs
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-785">
        {packages.map(pkg => <Card key={pkg.id} className={pkg.popular ? 'border-primary shadow-lg' : ''} data-id="element-786">
            <CardContent className="p-6" data-id="element-787">
              {pkg.popular && <div className="bg-primary text-primary-foreground text-xs font-medium px-2 py-1 rounded-full inline-block mb-3" data-id="element-788">
                  {pkg.discount}
                </div>}
              <h4 className="font-semibold text-lg mb-2" data-id="element-789">{pkg.name}</h4>
              <div className="mb-4" data-id="element-790">
                <span className="text-3xl font-bold" data-id="element-791">${pkg.price}</span>
                {pkg.isSubscription && <span className="text-muted-foreground ml-2" data-id="element-792">/month</span>}
              </div>
              <ul className="space-y-2 mb-6" data-id="element-793">
                <li className="flex items-center text-sm" data-id="element-794">
                  <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-795" />
                  {pkg.credits === -1 ? 'Unlimited' : pkg.credits} credits
                </li>
                <li className="flex items-center text-sm" data-id="element-796">
                  <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-797" />
                  {pkg.credits === -1 ? 'Unlimited' : `~${pkg.credits * 100}`}{' '}
                  comments
                </li>
                <li className="flex items-center text-sm" data-id="element-798">
                  <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-799" />
                  {pkg.isSubscription ? 'Renews monthly' : 'Never expires'}
                </li>
              </ul>
              <Button onClick={() => handlePurchase(pkg)} disabled={loading !== null} className="w-full" variant={pkg.popular ? 'default' : 'outline'} data-id="element-800">
                {loading === pkg.id ? <>
                    <Loader2 size={16} className="mr-2 animate-spin" data-id="element-801" />
                    Processing...
                  </> : <>
                    <CreditCard size={16} className="mr-2" data-id="element-802" />
                    Buy Now
                  </>}
              </Button>
            </CardContent>
          </Card>)}
      </div>
      <div className="bg-secondary/20 p-4 rounded-lg" data-id="element-803">
        <h4 className="font-medium text-sm mb-2" data-id="element-804">💳 Secure Payment</h4>
        <p className="text-xs text-muted-foreground" data-id="element-805">
          All payments are processed securely through Stripe. We never store
          your credit card information.
        </p>
      </div>
    </div>;
}